import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { compose } from 'redux'
import { Link, withRouter } from 'react-router-dom'
import { Button, Menu, Tag } from 'antd'
import { userActions } from '../Redux/_actions'
import { toggleSettingsDrawer } from '../Redux/slices/Preferences/preferences'
import styles from './TopBar.module.scss'
import PreferenceSettings from './PreferenceSettings'
import InstaController from './InstaController/InstaController'
import AppVersion from './AppVersion'
import ViewSubMenu from './MenuItems/ViewSubMenu'
import UserProfile from './UserProfile'
import { selectLibraries, selectTrainerSettings } from '../Redux/selectors/selectors'
import { useComponentLocale } from '../hooks/useComponentLocale'
import { setResetState, setResetTrainerInfo } from '../Redux/slices/trainer'
import { resetSessionStats } from '../Redux/slices/trainerSession'
import { BlogIcon, HandsIcon, InstaHandIcon, LibraryIcon, TrainerIcon, UploadIcon, ViewerIcon } from '../helpers/icons'
import { useActions } from '../hooks/useActions'
import classNames from 'classnames'
import ModalGpt from '../components/ModalGPT/ModalGPT'

const pathDictionary = {
	'/': [],
	'/library': 'library',
	'/viewer': 'viewer',
	'/hands': 'hands',
	'/upload': 'upload',
	'/settings/subscriptions': 'settings',
	'/docs/terms': 'docs',
	'/docs/refund': 'docs',
	// '/docs/GTOBaseNews': 'docs',
	'/trainer': 'trainer',
}

function TopBar({ location }) {
	const { librariesResponse } = useSelector(selectLibraries)
	const actionsHistory = useSelector((state) => state.trainerActionsHistory.tables[0].actionsHistory)
	const { isReady } = useSelector(selectTrainerSettings)
	const {
		setConfigurationId,
		setCrossFilters,
		setResetConfigDescription,
		setSpotLines,
		setFilterByHands,
		setHandsCountByFilter,
		setHandRange,
		setEquityThreshold,
		setEVThreshold,
	} = useActions()

	const [selectedIcon, setSelectedIcon] = useState(sessionStorage.getItem('icon') ?? '')
	const dispatch = useDispatch()

	const openedTab = location.pathname.includes('docs') ? location.pathname.slice(1, 5) : location.pathname.slice(1)

	const getLocale = useComponentLocale()
	const libraryLocale = getLocale({ component: 'topBar', section: 'library' })
	const viewLocale = getLocale({ component: 'topBar', section: 'view' })
	const handsLocale = getLocale({ component: 'topBar', section: 'hands' })
	const uploadLocale = getLocale({ component: 'topBar', section: 'upload' })
	const trainerLocale = getLocale({ component: 'topBar', section: 'trainer' })
	const instahandLocale = getLocale({ component: 'topBar', section: 'instahand' })
	const loginLocale = getLocale({ component: 'common_buttons', section: 'login' })
	const blogLocale = getLocale({ component: 'topBar', section: 'blog' })

	const getIconStyle = (el) => {
		return el === selectedIcon ? styles.selectedIcon : styles.topBarLinks
	}

	let user = localStorage.getItem('user')
	if (user) {
		user = JSON.parse(user)
	}

	const menuItemsNoAuthArr = [
		{
			key: 'libraryNoAuth',
			link: '/library',
			item: libraryLocale,
			icon: <LibraryIcon style={{ opacity: 0.4 }} />,
			isDisabled: true,
			isExternal: false,
		},
		{
			key: 'viewerNoAuth',
			link: '/',
			item: viewLocale,
			icon: <ViewerIcon style={{ opacity: 0.4 }} />,
			isDisabled: true,
			isExternal: false,
		},
		{
			key: 'handsNoAuth',
			link: '/hands',
			item: handsLocale,
			icon: <HandsIcon style={{ opacity: 0.4 }} />,
			isDisabled: true,
			isExternal: false,
		},
		{
			key: 'uploadNoAuth',
			link: '/upload',
			item: uploadLocale,
			icon: <UploadIcon style={{ opacity: 0.4 }} />,
			isDisabled: true,
			isExternal: false,
		},
		{
			key: 'trainerNoAuth',
			link: '/trainer',
			item: trainerLocale,
			icon: <TrainerIcon style={{ opacity: 0.4 }} />,
			isDisabled: true,
			isExternal: false,
		},
		// {
		// 	key: 'docsNoAuth',
		// 	link: '/docs/GTOBaseNews',
		// 	item: docsLocale,
		// 	icon: <span className={styles.docsIcon} />,
		// 	isDisabled: false,
		// },
		{
			key: 'blogNoAuth',
			// link: '/docs/GTOBaseNews',
			link: 'https://blog.gtobase.com',
			item: blogLocale,
			icon: <BlogIcon />,
			isDisabled: false,
			isExternal: true,
		},
	]

	const menuItemsAuthArr = [
		{
			key: 'library',
			link: '/library',
			item: libraryLocale,
			icon: <LibraryIcon />,
		},
		{
			key: 'viewer',
			link: '/',
			item: viewLocale,
			icon: <ViewerIcon />,
			isExternal: false,
		},
		{
			key: 'hands',
			link: '/hands',
			item: handsLocale,
			icon: <HandsIcon />,
			isExternal: false,
		},
		{
			key: 'upload',
			link: '/upload',
			item: uploadLocale,
			icon: <UploadIcon />,
			isExternal: false,
		},
		{
			key: 'trainer',
			link: '/trainer',
			item: trainerLocale,
			icon: <TrainerIcon />,
			isExternal: false,
		},
		{
			key: 'blog',
			link: 'https://blog.gtobase.com',
			item: blogLocale,
			icon: <BlogIcon />,
			isExternal: true,
		},
	]

	const toggleNewTraining = () => {
		setConfigurationId(null)
		setCrossFilters(null)
		setSpotLines(null)
		setFilterByHands(null)
		setHandsCountByFilter(null)
		setHandRange([0, 100])
		setResetConfigDescription()
		setEquityThreshold([0, 100])
		setEVThreshold([0, 100])
		dispatch(setResetTrainerInfo())
		dispatch(setResetState(null, 0))
		dispatch(resetSessionStats())
	}

	const path = location.pathname
	const { search } = location

	const historyLength = actionsHistory.length
	const isTrainerLiveMode = historyLength >= 1 && path !== '/trainer'

	// remove selection if selected key is main
	let selectedKey = pathDictionary[path]

	// check for nested values for viewer
	if (selectedKey === 'viewer') {
		if (search === '') {
			selectedKey = `viewer_107`
		} else {
			const viewerId = search.match('id=\\d*').toString()
			selectedKey = `viewer_${viewerId.replace('id=', '')}`
		}
	}

	useEffect(() => {
		if (user) {
			const params = new URLSearchParams(window.location.search)
			const query = params.get('q')
			// const spotId = Number(params.get('spot_id'))

			const isQuery = !!query
			dispatch(userActions.getLibraries(isQuery))
		}
	}, [])

	useEffect(() => {
		sessionStorage.setItem('icon', openedTab)
		setSelectedIcon(openedTab)
	}, [location.pathname])

	const onOpenInstaHandDrawer = () => {
		dispatch(userActions.openInstaHand())
	}

	const onLogout = useCallback(() => {
		dispatch(userActions.logout())
	}, [])

	const onOpenSettingsDrawer = useCallback(() => {
		dispatch(toggleSettingsDrawer(true))
	}, [])

	return (
		<>
			<Menu selectedKeys={selectedKey} mode='horizontal' className={styles.topBarMenu}>
				<div className={styles.logoContainer}>
					<Link to='/dashboard' className={styles.logoLink}>
						<div className={styles.logo} />
					</Link>

					<AppVersion user={user} />
				</div>
				<span style={{ flex: '1 1 0' }} />
				{user
					? menuItemsAuthArr.map((el) => {
							if (el.item !== viewLocale) {
								return (
									<Menu.Item
										key={el.key}
										onClick={() => isReady && el.key === 'trainer' && toggleNewTraining()}
										className={el.isExternal ? styles.menuItemExternalLink : styles.menuItem}
									>
										{el.isExternal ? (
											<a href={el.link} target={'_blank'} rel='noreferrer' className={classNames(styles.topBarLinks)}>
												{el.icon}
												<span className={styles.externalLinksTitle}>{el.item}</span>
											</a>
										) : (
											<Link to={el.link} className={styles.topBarLinks}>
												{el.icon}
												<span>{el.item}</span>
												{el.item === trainerLocale && isTrainerLiveMode && (
													<Tag style={{ background: 'darkred', marginLeft: 3 }}>Live</Tag>
												)}
											</Link>
										)}
									</Menu.Item>
								)
							}

							return (
								<ViewSubMenu
									key={el.key}
									librariesResponse={librariesResponse}
									viewLocale={viewLocale}
									getIconStyle={getIconStyle}
								/>
							)
					  })
					: menuItemsNoAuthArr.map((el) => {
							return (
								<Menu.Item
									key={el.key}
									disabled={el.isDisabled}
									className={el.isExternal ? styles.menuItemExternalLink : styles.menuItem}
								>
									{el.isExternal ? (
										<a href={el.link} target={'_blank'} rel='noreferrer' className={styles.topBarLinks}>
											{el.icon}
											<span>{el.item}</span>
										</a>
									) : (
										<Link to={el.link} className={styles.topBarLinksDisabled}>
											{el.icon}
											<span>{el.item}</span>
										</Link>
									)}
								</Menu.Item>
							)
					  })}

				<span className={user ? styles.topBarSpanAfterMenuLogin : styles.topBarSpanAfterMenuLogout} />

				{/* <Divider type='vertical' className={styles.topBarDivider} /> */}

				{/* <LanguageSelector /> */}

				{user ? (
					<>
						<PreferenceSettings onOpenSettingsDrawer={onOpenSettingsDrawer} />

						<Menu.Item
							style={{ borderBottom: 'none', marginLeft: 10, marginRight: 0, padding: 0 }}
							className={styles.activeTab}
						>
							<button onClick={onOpenInstaHandDrawer} className={styles.instaHandButton}>
								<InstaHandIcon />
								<div>{instahandLocale}</div>
							</button>
						</Menu.Item>

						<UserProfile onLogout={onLogout} userName={user.username} />
					</>
				) : (
					<>
						<Menu.Item style={{ borderBottom: 'none', margin: 0 }} className={styles.activeTab}>
							<Link to='/'>
								<Button shape='round' type='link' style={{ alignSelf: 'center' }}>
									{loginLocale}
								</Button>
							</Link>
						</Menu.Item>
					</>
				)}
			</Menu>
			{user ? (
				<>
					<ModalGpt source={window.location.pathname.split('/')[1]} />
					<InstaController />
				</>
			) : null}
		</>
	)
}

export default compose(withRouter)(TopBar)
